/**
 * Transport over an OPEN, client-owned RTCDataChannel created with `{ordered: false, maxRetransmits: 0}`
 * (UDP-like). Only this file touches the DataChannel API.
 */
import type { Transport, TransportState } from '@hf/shared';
import { TransportCore } from './transport-core';
import type { CreateWebRtcTransport } from './types';

/**
 * Drop instead of queueing when the SCTP send buffer backs up: a stale input packet is worthless (the next one
 * carries the same inputs redundantly) and a growing queue is pure latency.
 */
export const WEBRTC_MAX_BUFFERED_BYTES = 64 * 1024;

const mapState = (s: RTCDataChannelState): TransportState =>
  s === 'open' ? 'open' : s === 'connecting' ? 'connecting' : 'closed';

export const createWebRtcTransport: CreateWebRtcTransport = (dc) => {
  const core = new TransportCore('webrtc', mapState(dc.readyState));
  try {
    dc.binaryType = 'arraybuffer';
  } catch {
    // Some implementations throw on unsupported values; Blob payloads are converted below.
  }

  const onMessage = (ev: MessageEvent): void => {
    const d: unknown = ev.data;
    if (d instanceof ArrayBuffer) core.emit(new Uint8Array(d));
    else if (ArrayBuffer.isView(d)) core.emit(new Uint8Array(d.buffer, d.byteOffset, d.byteLength));
    else if (typeof Blob !== 'undefined' && d instanceof Blob) {
      void d.arrayBuffer().then((buf) => core.emit(new Uint8Array(buf)));
    }
    // Strings are not game packets: ignored.
  };
  const onOpen = (): void => core.setState('open');
  const onClose = (): void => {
    core.setState('closed');
    detach();
  };
  dc.addEventListener('message', onMessage);
  dc.addEventListener('open', onOpen);
  dc.addEventListener('close', onClose);
  dc.addEventListener('closing', onClose);
  dc.addEventListener('error', onClose);
  const detach = (): void => {
    dc.removeEventListener('message', onMessage);
    dc.removeEventListener('open', onOpen);
    dc.removeEventListener('close', onClose);
    dc.removeEventListener('closing', onClose);
    dc.removeEventListener('error', onClose);
  };

  const transport: Transport = {
    kind: 'webrtc',
    get state() {
      return core.state;
    },
    stats: core.stats,
    send(data) {
      if (core.state !== 'open' || dc.readyState !== 'open') return;
      if (dc.bufferedAmount > WEBRTC_MAX_BUFFERED_BYTES) return;
      try {
        dc.send(data as Uint8Array<ArrayBuffer>);
        core.countSent(data.byteLength);
      } catch {
        // Closed between the check and the send, or the buffer is full: unreliable channel, just drop.
      }
    },
    onMessage: (h) => core.onMessage(h),
    onStateChange: (h) => core.onStateChange(h),
    reportRtt: (ms) => core.reportRtt(ms),
    close() {
      if (core.state === 'closed') return;
      detach();
      core.setState('closed');
      try {
        dc.close();
      } catch {
        // already closed
      }
    },
  };
  return transport;
};
